/** Keep in sync with inventozen-api and inventozen-frontpublic */

export const CMS_SEPARATOR_TYPES = ["NONE", "SOLID", "DASHED", "DOTTED"] as const;
export type CmsSeparatorType = (typeof CMS_SEPARATOR_TYPES)[number];

export const CMS_FONT_SIZES_PX = [
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "30px",
  "36px",
] as const;

/** Plage autorisée pour une taille de texte libre (en plus des presets). */
export const CMS_FONT_SIZE_MIN_PX = 8;
export const CMS_FONT_SIZE_MAX_PX = 96;

const FONT_VARIANTS_REGULAR_ONLY = [
  { id: "regular", label: "Regular", weight: 400 },
] as const;

const FONT_VARIANTS_REGULAR_TO_BOLD = [
  { id: "regular", label: "Regular", weight: 400 },
  { id: "medium", label: "Medium", weight: 500 },
  { id: "semibold", label: "Semibold", weight: 600 },
  { id: "bold", label: "Bold", weight: 700 },
] as const;

const FONT_VARIANTS_LIGHT_TO_BOLD = [
  { id: "light", label: "Light", weight: 300 },
  { id: "regular", label: "Regular", weight: 400 },
  { id: "medium", label: "Medium", weight: 500 },
  { id: "semibold", label: "Semibold", weight: 600 },
  { id: "bold", label: "Bold", weight: 700 },
] as const;

const FONT_VARIANTS_REGULAR_AND_BOLD = [
  { id: "regular", label: "Regular", weight: 400 },
  { id: "bold", label: "Bold", weight: 700 },
] as const;

export const CMS_GOOGLE_FONTS = [
  {
    id: "instrument-sans",
    label: "Instrument Sans",
    googleFontFamily: "Instrument Sans",
    variants: FONT_VARIANTS_REGULAR_TO_BOLD,
  },
  {
    id: "roboto",
    label: "Roboto",
    googleFontFamily: "Roboto",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "inter",
    label: "Inter",
    googleFontFamily: "Inter",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "open-sans",
    label: "Open Sans",
    googleFontFamily: "Open Sans",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "lato",
    label: "Lato",
    googleFontFamily: "Lato",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "montserrat",
    label: "Montserrat",
    googleFontFamily: "Montserrat",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "poppins",
    label: "Poppins",
    googleFontFamily: "Poppins",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "oswald",
    label: "Oswald",
    googleFontFamily: "Oswald",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "raleway",
    label: "Raleway",
    googleFontFamily: "Raleway",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "nunito",
    label: "Nunito",
    googleFontFamily: "Nunito",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "source-sans-3",
    label: "Source Sans 3",
    googleFontFamily: "Source Sans 3",
    variants: FONT_VARIANTS_LIGHT_TO_BOLD,
  },
  {
    id: "pt-sans",
    label: "PT Sans",
    googleFontFamily: "PT Sans",
    variants: FONT_VARIANTS_REGULAR_AND_BOLD,
  },
  {
    id: "merriweather",
    label: "Merriweather",
    googleFontFamily: "Merriweather",
    variants: FONT_VARIANTS_REGULAR_AND_BOLD,
  },
  {
    id: "playfair-display",
    label: "Playfair Display",
    googleFontFamily: "Playfair Display",
    variants: FONT_VARIANTS_REGULAR_TO_BOLD,
  },
  {
    id: "libre-baskerville",
    label: "Libre Baskerville",
    googleFontFamily: "Libre Baskerville",
    variants: FONT_VARIANTS_REGULAR_AND_BOLD,
  },
  {
    id: "dancing-script",
    label: "Dancing Script",
    googleFontFamily: "Dancing Script",
    variants: FONT_VARIANTS_REGULAR_TO_BOLD,
  },
  {
    id: "caveat",
    label: "Caveat",
    googleFontFamily: "Caveat",
    variants: FONT_VARIANTS_REGULAR_TO_BOLD,
  },
  {
    id: "pacifico",
    label: "Pacifico",
    googleFontFamily: "Pacifico",
    variants: FONT_VARIANTS_REGULAR_ONLY,
  },
  {
    id: "lobster",
    label: "Lobster",
    googleFontFamily: "Lobster",
    variants: FONT_VARIANTS_REGULAR_ONLY,
  },
  {
    id: "abril-fatface",
    label: "Abril Fatface",
    googleFontFamily: "Abril Fatface",
    variants: FONT_VARIANTS_REGULAR_ONLY,
  },
  {
    id: "bebas-neue",
    label: "Bebas Neue",
    googleFontFamily: "Bebas Neue",
    variants: FONT_VARIANTS_REGULAR_ONLY,
  },
] as const;

export type CmsGoogleFontDefinition = (typeof CMS_GOOGLE_FONTS)[number];
export type CmsGoogleFontVariant =
  (typeof CMS_GOOGLE_FONTS)[number]["variants"][number];
export type CmsFontVariantId =
  (typeof CMS_GOOGLE_FONTS)[number]["variants"][number]["id"];
export type CmsTypographySelection = {
  fontFamily: string | null;
  fontVariant: string | null;
};
export const DEFAULT_CMS_FONT_FAMILY = "Instrument Sans";
export const DEFAULT_CMS_FONT_VARIANT = "regular";

export const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

export type CmsColor = {
  id: string;
  name: string;
  hex: string;
  sortOrder: number;
};

export type CmsThemeStyle = {
  backgroundColor: string;
  textColor: string;
  separatorType: CmsSeparatorType;
  separatorColor: string;
  buttonColor?: string;
  buttonTextColor?: string;
  fontFamily: string | null;
  fontVariant: string | null;
};

export type CmsThemeShellConfig = {
  backgroundColorId: string | null;
  textColorId: string | null;
  separatorType: CmsSeparatorType;
  separatorColorId: string | null;
  buttonColorId?: string | null;
  fontFamily: string | null;
  fontVariant: string | null;
};

/** Transparent select triggers in CMS forms and TipTap toolbar */
export const CMS_FORM_SELECT_TRIGGER_CLASS =
  "bg-transparent shadow-none border-slate-200";

export type CmsTheme = {
  id: string;
  colors: CmsColor[];
  defaultPageBackgroundColorId: string | null;
  defaultPageBackgroundColor: string;
  defaultPageTextColorId: string | null;
  defaultPageTextColor: string;
  /** Couleur des liens de contenu au survol/focus/clic (vide = pas de changement). */
  defaultPageLinkColorId: string | null;
  defaultPageLinkColor: string;
  defaultPageAltBackgroundColorId: string | null;
  defaultPageAltBackgroundColor: string;
  defaultPageAltTextColorId: string | null;
  defaultPageAltTextColor: string;
  defaultPageFontFamily: string | null;
  defaultPageFontVariant: string | null;
  header: CmsThemeStyle;
  footer: CmsThemeStyle;
  headerConfig: CmsThemeShellConfig;
  footerConfig: CmsThemeShellConfig;
};

export function isValidHex(hex: string): boolean {
  return HEX_COLOR_REGEX.test(hex);
}

export const CMS_PAGE_TEXT_COLOR_MODES = [
  "AUTO",
  "THEME_TEXT",
  "CUSTOM",
] as const;

export type CmsPageTextColorMode = (typeof CMS_PAGE_TEXT_COLOR_MODES)[number];

export function getGoogleFontDefinitionByFamily(
  fontFamily: string | null | undefined,
): CmsGoogleFontDefinition | undefined {
  return CMS_GOOGLE_FONTS.find(
    (font) =>
      font.googleFontFamily.toLowerCase() === (fontFamily ?? "").toLowerCase(),
  );
}

export function getGoogleFontDefinitionById(
  fontId: string | null | undefined,
): CmsGoogleFontDefinition | undefined {
  return CMS_GOOGLE_FONTS.find((font) => font.id === fontId);
}

export function getCmsFontVariants(
  fontFamily: string | null | undefined,
): readonly CmsGoogleFontVariant[] {
  return (
    getGoogleFontDefinitionByFamily(fontFamily)?.variants ??
    getGoogleFontDefinitionByFamily(DEFAULT_CMS_FONT_FAMILY)?.variants ??
    FONT_VARIANTS_REGULAR_ONLY
  );
}

export function resolveCmsFontVariant(
  fontFamily: string | null | undefined,
  fontVariant: string | null | undefined,
): CmsGoogleFontVariant {
  const variants = getCmsFontVariants(fontFamily);
  return (
    variants.find((variant) => variant.id === fontVariant) ??
    variants.find((variant) => variant.id === DEFAULT_CMS_FONT_VARIANT) ??
    variants[0]
  );
}

export function normalizeCmsTypography(
  fontFamily: string | null | undefined,
  fontVariant: string | null | undefined,
): CmsTypographySelection {
  const family = getGoogleFontDefinitionByFamily(fontFamily)?.googleFontFamily ?? null;
  if (!family) {
    return { fontFamily: null, fontVariant: null };
  }

  return {
    fontFamily: family,
    fontVariant: resolveCmsFontVariant(family, fontVariant).id,
  };
}

export function resolveCmsFontWeight(
  fontFamily: string | null | undefined,
  fontVariant: string | null | undefined,
): number {
  return resolveCmsFontVariant(fontFamily, fontVariant).weight;
}

export function resolveAllowedFontWeight(raw: string): number | null {
  const normalized = raw.trim();
  if (!/^\d+$/.test(normalized)) return null;
  const weight = Number(normalized);
  if (![300, 400, 500, 600, 700].includes(weight)) return null;
  return weight;
}

export function resolveColor(
  palette: CmsColor[],
  colorId: string | null | undefined,
  fallback = "#FFFFFF",
): string {
  if (!colorId) return fallback;
  return palette.find((c) => c.id === colorId)?.hex ?? fallback;
}

function getRelativeLuminance(hex: string): number {
  const normalized = hex.replace(/^#/, "");
  if (normalized.length !== 6) return 1;

  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

  return (
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  );
}

export function getContrastTextColor(
  backgroundHex: string,
  options?: { light?: string; dark?: string },
): string {
  const light = options?.light ?? "#FFFFFF";
  const dark = options?.dark ?? "#171717";
  const bg = backgroundHex?.trim();

  if (!bg || !/^#[0-9A-Fa-f]{6}$/.test(bg)) return dark;

  return getRelativeLuminance(bg) > 0.179 ? dark : light;
}

export function findPaletteTextColorId(palette: CmsColor[]): string | null {
  const byName = palette.find((c) => c.name.trim().toLowerCase() === "texte");
  if (byName) return byName.id;
  const sorted = [...palette].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted[1]?.id ?? null;
}

export function resolveThemeDefaultPageTextColor(
  theme: CmsTheme,
): string | null {
  const colorId =
    theme.defaultPageTextColorId ?? findPaletteTextColorId(theme.colors);
  if (colorId) {
    return resolveColor(
      theme.colors,
      colorId,
      theme.defaultPageTextColor ?? "#171717",
    );
  }
  const hex = theme.defaultPageTextColor?.trim();
  return hex || null;
}

export function resolvePageBackground(
  theme: CmsTheme | null | undefined,
  pageBackgroundColorId?: string | null,
): string {
  if (!theme) return "#FFFFFF";
  return resolveColor(
    theme.colors,
    pageBackgroundColorId ?? theme.defaultPageBackgroundColorId,
    theme.defaultPageBackgroundColor,
  );
}

export function resolvePageForeground(
  theme: CmsTheme | null | undefined,
  page: {
    pageBackgroundColorId?: string | null;
    pageTextColorMode?: CmsPageTextColorMode | null;
    pageTextColorId?: string | null;
  },
): string {
  if (!theme) return "#171717";

  const background = resolvePageBackground(theme, page.pageBackgroundColorId);
  const mode = page.pageTextColorMode ?? "AUTO";

  if (mode === "THEME_TEXT") {
    return resolveThemeDefaultPageTextColor(theme) ?? "#171717";
  }

  if (mode === "CUSTOM") {
    return resolveColor(
      theme.colors,
      page.pageTextColorId,
      getContrastTextColor(background),
    );
  }

  const themeText = resolveThemeDefaultPageTextColor(theme);
  if (themeText) return themeText;

  return getContrastTextColor(background);
}

export const GOOGLE_FONTS_CSS_URL =
  "https://fonts.googleapis.com/css2?" +
  CMS_GOOGLE_FONTS.map(
    (font) =>
      `family=${encodeURIComponent(font.googleFontFamily).replace(/%20/g, "+")}:wght@${[
        ...new Set(font.variants.map((variant) => variant.weight)),
      ].join(";")}`,
  ).join("&") +
  "&display=swap";
